import { Button, ScoreRing } from '@qi/ui';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useFunnelScore } from '../lib/funnel-score.ts';
import { useSessionStore } from '../stores/session-store.ts';

export const Route = createFileRoute('/_funnel/start/scores')({
  beforeLoad: () => {
    const { faceSummary, voiceSummary } = useSessionStore.getState();
    if (!faceSummary || !voiceSummary) throw redirect({ to: '/start' });
  },
  component: FunnelScoresPage,
});

// Sub-metrics the cold scan actually measured (no sweep). Labelled in plain
// language, not clinical terms.
const READOUT: { key: 'focus' | 'emotionalCoherence' | 'calm'; label: string }[] = [
  { key: 'focus', label: 'Focus' },
  { key: 'emotionalCoherence', label: 'Balance' },
  { key: 'calm', label: 'Calm' },
];

function FunnelScoresPage() {
  const navigate = useNavigate();
  const { composite, scores, isLoading, isError } = useFunnelScore();

  if (isLoading) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-md items-center justify-center px-6">
        <p className="animate-pulse font-mono text-xs uppercase tracking-[0.32em] text-aurora">
          Reading your signals…
        </p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="mx-auto max-w-md space-y-4 px-6 py-16 text-center">
        <p className="text-ink-1">We couldn't read your scan. Let's try once more.</p>
        <Button variant="resonance" size="lg" onClick={() => navigate({ to: '/start/bioscan' })}>
          Rescan
        </Button>
      </div>
    );
  }

  const values: Record<(typeof READOUT)[number]['key'], number> = {
    focus: scores.focus,
    emotionalCoherence: scores.emotionalCoherence,
    calm: 100 - scores.stressLoad,
  };

  return (
    <div className="mx-auto max-w-md space-y-8 px-6 py-12">
      <header className="space-y-3 text-center">
        <p className="font-mono text-xs uppercase tracking-[0.32em] text-aurora">Your read</p>
        <h1 className="font-display text-3xl text-ink-0">Here's where you are right now</h1>
        <p className="text-pretty text-ink-1">
          A snapshot from your face and voice. This is your starting point — a baseline to build on.
        </p>
      </header>

      <div className="flex justify-center">
        <ScoreRing score={composite} size={220} label="Resonance" showScale tone="after" />
      </div>

      <div className="grid grid-cols-3 gap-3">
        {READOUT.map(({ key, label }) => (
          <div key={key} className="qi-glass rounded-lg p-4 text-center">
            <p className="font-mono text-2xl tabular-nums text-ink-0">{Math.round(values[key])}</p>
            <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.14em] text-ink-3">
              {label}
            </p>
          </div>
        ))}
      </div>

      <div className="space-y-3">
        <Button
          variant="resonance"
          size="lg"
          className="w-full"
          onClick={() => navigate({ to: '/start/calibrate' })}
        >
          Tune my resonance
        </Button>
        <p className="text-center text-xs text-ink-3">
          A 30-second calibration session, then we'll show you what shifts.
        </p>
      </div>
    </div>
  );
}
